import React from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { DropzoneInputProps } from 'react-dropzone';

import { IntegrationBar } from './integration-bar';
import { allowedFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { cn } from '../../lib/utils';
import { RelativityWorkspaceType, RelativityListType } from '@/api/types/backend-dto-types';
import { RelativityIntegrationModal } from '@/new/components/dialogs/relativity/relativity-integration-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { ApideckConnection } from '@/types';

interface FileUploaderProps {
  maxFiles: number;
  matterId?: string;
  subsetId?: string | null;
  onApideckSuccess?: () => void;
  isApideckEnabled?: boolean;
  disabled?: boolean;
  // Dropzone props
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  isDragActive: boolean;
  open: () => void;
  // useFileUploadLogic props
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  modalContent: React.ReactNode | null;
  isRelativityModalOpen: boolean;
  setIsRelativityModalOpen: (open: boolean) => void;
  apiDeckSessionToken: string | null;
  apiDeckConnections: Array<ApideckConnection>;
  connectRelativity: () => Promise<void>;
  handleOpenApideckModal: (connection: string, modalContent: React.ReactNode) => Promise<void>;
  handleSelectRelativityList: (workspace: RelativityWorkspaceType, list: RelativityListType) => Promise<void>;
}

export function FileUploadInput({
  maxFiles,
  matterId,
  subsetId,
  onApideckSuccess,
  isApideckEnabled,
  disabled,
  // Dropzone props
  getInputProps,
  isDragActive,
  open,
  // useFileUploadLogic props
  modalOpen,
  setModalOpen,
  modalContent,
  isRelativityModalOpen,
  setIsRelativityModalOpen,
  apiDeckSessionToken,
  apiDeckConnections,
  connectRelativity,
  handleOpenApideckModal,
  handleSelectRelativityList,
}: FileUploaderProps) {
  const handleApideckModal = (connection: string, modalContent: React.ReactNode) => {
    handleOpenApideckModal(connection, modalContent);
  };

  return (
    <>
      <input {...getInputProps()} data-testid="upload-files-input" />

      <div className="-mt-8 flex max-w-xs flex-col items-center gap-2">
        {matterId && (
          <div className={cn(disabled && 'pointer-events-none opacity-70')}>
            <IntegrationBar
              matterId={matterId}
              isApideckEnabled={isApideckEnabled}
              apiDeckConnections={apiDeckConnections}
              apiDeckSessionToken={apiDeckSessionToken}
              onRelativityConnect={connectRelativity}
              onApideckModal={handleApideckModal}
              onApideckSuccess={onApideckSuccess}
            />
          </div>
        )}

        <div className="flex w-full justify-between gap-2 px-2 text-xs text-gray-600">
          <p className="flex items-center gap-1.5">
            Max. {maxFiles.toLocaleString()} files{' '}
            <Popover>
              <PopoverTrigger>
                <Icon className="lucide--circle-question-mark" />
              </PopoverTrigger>
              <PopoverContent side="top" sideOffset={11} className="max-w-sm text-xs text-gray-700">
                Wexler currently supports up to 200,000 documents in a single matter. If this isn't sufficient for your
                use case, reach out to{' '}
                <a href="mailto:support@wexler.ai" className="text-blue-500">
                  support@wexler.ai
                </a>
                .
              </PopoverContent>
            </Popover>
          </p>
          <p>Max. 3GB per file</p>
        </div>

        <div
          className={cn(
            'z-50 flex h-68 w-full flex-col items-center justify-center gap-4 rounded-xl px-12 duration-150',
            disabled
              ? 'pointer-events-none'
              : isDragActive
                ? 'border border-dashed border-blue-500 bg-white shadow-lg/8 shadow-blue-500'
                : 'border border-dashed border-gray-400 bg-white shadow-lg/1 shadow-blue-500',
          )}
        >
          <AnimatePresence mode="wait">
            {disabled ? (
              <motion.p
                key="disabled"
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(2px)' }}
                transition={{ duration: 0.1 }}
                className="max-w-52 text-center text-balance text-gray-500"
              >
                <Icon className="lucide--info mr-1.5 -mb-0.5 inline-block shrink-0 text-gray-400" />
                Please wait for the current upload to finish before adding more files.
              </motion.p>
            ) : (
              <motion.div
                key="enabled"
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(2px)' }}
                transition={{ duration: 0.1 }}
                className="flex flex-col items-center gap-4"
              >
                <div
                  onClick={open}
                  className={`${isDragActive ? 'border-blue-300 bg-blue-50 text-blue-400' : 'bg-gray-100 text-gray-400 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-400'} flex cursor-pointer items-center justify-center rounded-md border p-2 pb-1.75 text-lg duration-150`}
                >
                  <Icon className="lucide--upload-cloud" />
                </div>

                <p className="max-w-52 text-center text-balance text-gray-500">
                  Drag and drop documents here or{' '}
                  <Button onClick={open} className="p-0 text-blue-500 hover:text-blue-600" variant="ghost">
                    click to browse files.
                  </Button>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <Dialog>
          <DialogTrigger className="flex items-center gap-1.5 text-xs">
            <Button size="sm">
              <Icon className="lucide--file-check-2" />
              Accepted file types
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <Icon className="lucide--file-check-2" /> Accepted File Types
              </DialogTitle>
              <DialogDescription>The range of file types currently supported by Wexler.</DialogDescription>
            </DialogHeader>

            <div className="my-5 flex flex-wrap gap-1.5 text-xs">
              {allowedFileTypes.map((ext) => (
                <span key={ext} className="rounded-md border border-gray-300 bg-gray-50 p-0.75 px-1.75 text-gray-600">
                  {ext}
                </span>
              ))}
            </div>

            <p>
              The maximum size of any one file is 3GB. Reach out to{' '}
              <a href="mailto:support@wexler.ai" className="text-blue-500">
                the Wexler team
              </a>{' '}
              if you have additional file requirements.
            </p>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>{modalContent}</DialogContent>
      </Dialog>

      <Dialog open={isRelativityModalOpen} onOpenChange={setIsRelativityModalOpen}>
        <DialogContent className="max-w-4xl">
          <RelativityIntegrationModal
            matterId={matterId}
            caseId={subsetId ?? ''}
            onSelectList={handleSelectRelativityList}
            onClose={() => setIsRelativityModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
